import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getNetlifyFunctionsBase } from "../../utils/netlifyFunctions.js";
import { analyzeMentalPatterns } from "../../utils/mentalPatternAnalysis.js";
import { analyzeAngerPatterns } from "../../utils/angerAnalysis.js";
import { analyzeWellness } from "../../utils/wellnessAnalysis.js";
import { analyzeGad7 } from "../../utils/gad7Analysis.js";
import { buildMentalPatternsHubInsights, buildLockedPatternTease } from "../../utils/mentalPatternsHubInsights.js";

const MentalPatternsContext = createContext(null);

export function MentalPatternsProvider({ verifiedEmail, children }) {
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!verifiedEmail) {
      setInsights([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/getUserInsights`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: verifiedEmail })
        });
        if (!response.ok) {
          const errText = await response.text();
          let apiError = "Unable to load insights.";
          try {
            const parsed = JSON.parse(errText);
            if (parsed && typeof parsed.error === "string") apiError = parsed.error;
          } catch (_) {}
          throw new Error(apiError);
        }
        const data = await response.json();
        const list = Array.isArray(data.insights) ? data.insights : [];
        if (!cancelled) {
          setInsights(list);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err?.message || "";
          const friendly =
            msg === "Failed to fetch"
              ? "Connection problem. Check your network and try again."
              : msg || "Unable to load your insights right now.";
          setError(friendly);
          setInsights([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [verifiedEmail, retryCount]);

  const stressAnalysis = useMemo(() => analyzeMentalPatterns(insights), [insights]);
  const angerAnalysis = useMemo(() => analyzeAngerPatterns(insights), [insights]);
  const wellness = useMemo(() => analyzeWellness(insights), [insights]);
  const gad7Analysis = useMemo(() => analyzeGad7(insights), [insights]);
  const hubInsights = useMemo(
    () => buildMentalPatternsHubInsights(insights, stressAnalysis, wellness, angerAnalysis, gad7Analysis),
    [insights, stressAnalysis, wellness, angerAnalysis, gad7Analysis]
  );
  const lockedPatternTease = useMemo(
    () => buildLockedPatternTease(insights, hubInsights),
    [insights, hubInsights]
  );

  const retry = () => {
    setError("");
    setIsLoading(true);
    setRetryCount((c) => c + 1);
  };

  const value = useMemo(
    () => ({
      insights,
      isLoading,
      error,
      retry,
      stressAnalysis,
      angerAnalysis,
      wellness,
      gad7Analysis,
      hubInsights,
      lockedPatternTease,
      totalReflections: stressAnalysis.totalReflections
    }),
    [insights, isLoading, error, stressAnalysis, angerAnalysis, wellness, gad7Analysis, hubInsights, lockedPatternTease]
  );

  return <MentalPatternsContext.Provider value={value}>{children}</MentalPatternsContext.Provider>;
}

export function useMentalPatterns() {
  const ctx = useContext(MentalPatternsContext);
  if (!ctx) {
    throw new Error("useMentalPatterns must be used within MentalPatternsProvider");
  }
  return ctx;
}
